import {
  type CreateDemoPublicBookingRequest,
  CreatePublicBookingRequestSchema,
  type DemoPublicBookingReceipt,
  type DemoPublicSchedulingConfiguration,
  type DemoSchedulingHours,
  PUBLIC_TERMS_CHANGED_ERROR_CODE,
  PublicAvailabilityRequestSchema,
  type PublicAvailabilityResponse,
  PublicAvailabilityResponseSchema,
  PublicBookingConfigurationSchema,
  type PublicBookingConsent,
  type PublicBookingConsentDocuments,
  type PublicBookingResponse,
  PublicBookingResponseSchema,
  VerifyPublicBookingPatientRequestSchema,
  type VerifyPublicBookingPatientResponse,
  VerifyPublicBookingPatientResponseSchema,
} from "@emr/contracts";
import { termsChanged } from "../consent/consentChoices";
import { DemoBookingApiError } from "./demoSchedulingClient";

export const demoSchedulingEnabled = () => import.meta.env.VITE_DEMO_SCHEDULING_ENABLED === "true";

export interface AvailabilitySearch {
  readonly fromDate: string;
  readonly toDate: string;
  readonly appointmentTypeId: string;
  /** Only for visit types where the patient chooses the physician. */
  readonly providerId?: string;
}

/** What an existing patient types to confirm they can book: never kept beyond the request. */
export interface PatientVerificationInput {
  readonly healthNumber: string;
  readonly dateOfBirth: string;
}

/**
 * A clinic's booking settings as the booking page reads them. The clinic lane adds its security
 * check's site key, the terms the patient agrees to, the clinic's weekly hours and the websites
 * that may show its booking form (PA-12; absent from an older API, read as none); the synthetic
 * demo has none of them.
 */
export type PatientBookingConfiguration = DemoPublicSchedulingConfiguration & {
  readonly turnstileSiteKey?: string;
  readonly consentDocuments?: PublicBookingConsentDocuments;
  readonly clinicHours?: readonly DemoSchedulingHours[] | undefined;
  readonly websiteOrigins?: readonly string[] | undefined;
};

/** What a clinic-lane booking carries beyond the booking itself; the demo lane ignores it. */
export interface PublicBookingExtras {
  readonly turnstileToken?: string;
  readonly verification?: string;
  /** The ticked terms (and the messaging line, when shown), naming the versions on screen. */
  readonly consent?: PublicBookingConsent;
  /**
   * A returning patient's optional Ontario health card number, at a clinic that books anyone
   * (PA-10). Sent as its 10 digits in the POST body only, and only when given.
   */
  readonly healthNumber?: string;
}

/**
 * `verification` is an existing patient's booking pass. It travels only in POST bodies, never
 * in a URL, and the page keeps it in memory only.
 */
export interface PatientSchedulingClient {
  configuration(signal?: AbortSignal): Promise<PatientBookingConfiguration>;
  availability(
    input: AvailabilitySearch,
    signal?: AbortSignal,
    verification?: string,
  ): Promise<PublicAvailabilityResponse>;
  createAppointment(
    input: CreateDemoPublicBookingRequest,
    signal?: AbortSignal,
    extras?: PublicBookingExtras,
  ): Promise<PublicBookingResponse | DemoPublicBookingReceipt>;
  /** Absent where the lane cannot verify existing patients (the synthetic demo). */
  verify?(
    input: PatientVerificationInput,
    turnstileToken: string,
    signal?: AbortSignal,
  ): Promise<VerifyPublicBookingPatientResponse>;
}

/** The service's error code (for example CHALLENGE_FAILED), never its message text. */
async function errorCode(response: Response): Promise<string | undefined> {
  const body = (await response.json().catch(() => null)) as {
    readonly error?: { readonly code?: unknown };
  } | null;
  const code = body?.error?.code;
  return typeof code === "string" && /^[A-Z_]{1,40}$/.test(code) ? code : undefined;
}

export function createPublicSchedulingClient(
  clinicSlug: string | undefined,
): PatientSchedulingClient {
  async function request(path: string, body?: unknown, signal?: AbortSignal): Promise<unknown> {
    if (!clinicSlug || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(clinicSlug) || clinicSlug.length > 80) {
      throw new Error("Open the booking link provided by your clinic.");
    }
    const origin = (import.meta.env.VITE_AGENT_API_ORIGIN || "http://127.0.0.1:4100").replace(
      /\/$/,
      "",
    );
    const response = await fetch(
      `${origin}/v1/public/${encodeURIComponent(clinicSlug)}/booking${path}`,
      {
        method: body === undefined ? "GET" : "POST",
        ...(body === undefined
          ? {}
          : { headers: { "content-type": "application/json" }, body: JSON.stringify(body) }),
        ...(signal === undefined ? {} : { signal }),
        cache: "no-store",
        credentials: "omit",
        referrerPolicy: "no-referrer",
      },
    );
    if (!response.ok) {
      // Do not echo untrusted response bodies that might accidentally include identity data.
      const code = await errorCode(response);
      throw new DemoBookingApiError(
        code === PUBLIC_TERMS_CHANGED_ERROR_CODE
          ? termsChanged
          : response.status === 409
            ? "That time is no longer available. Choose another appointment."
            : "Booking is temporarily unavailable. Please try again or contact the clinic.",
        response.status,
        code,
      );
    }
    return response.json();
  }
  return {
    async configuration(signal) {
      const configuration = PublicBookingConfigurationSchema.parse(
        await request("/configuration", undefined, signal),
      );
      if (configuration.clinicSlug !== clinicSlug) {
        throw new Error("This booking link could not be verified for the clinic.");
      }
      return configuration;
    },
    async availability(input, signal, verification) {
      const body = PublicAvailabilityRequestSchema.parse({
        ...input,
        ...(verification === undefined ? {} : { verification }),
      });
      return PublicAvailabilityResponseSchema.parse(await request("/availability", body, signal));
    },
    async createAppointment(input, signal, extras = {}) {
      const { turnstileToken, verification, consent, healthNumber } = extras;
      const body = CreatePublicBookingRequestSchema.parse({
        ...input,
        turnstileToken,
        consent,
        ...(verification === undefined ? {} : { verification }),
        ...(healthNumber === undefined ? {} : { healthNumber }),
      });
      return PublicBookingResponseSchema.parse(await request("/appointments", body, signal));
    },
    async verify(input, turnstileToken, signal) {
      const body = VerifyPublicBookingPatientRequestSchema.parse({ ...input, turnstileToken });
      return VerifyPublicBookingPatientResponseSchema.parse(await request("/verify", body, signal));
    },
  };
}
